import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';

interface SkillCategory {
  name: string;
  skills: string[];
}

const skillCategories: SkillCategory[] = [
  {
    name: 'skills.categories.frameworks',
    skills: ['React', 'React Native', 'Expo', 'SpringBoot', 'ExpressJs', 'Django']
  },
  {
    name: 'skills.categories.libraries',
    skills: ['Axios', 'ContextAPI', 'Scikit-learn', 'TensorFlow', 'OpenCV', 'Firebase', 'Matplotlib', 'Pandas', 'NumPy']
  },
  {
    name: 'skills.categories.programming',
    skills: ['Typescript', 'JavaScript', 'Java', 'Python', 'PHP', 'C', 'SQL', 'PL/SQL', 'TransactSQL']
  },
  {
    name: 'skills.categories.theoretical',
    skills: ['API design', 'MVC', 'Design Patterns & Principles (S.O.L.I.D)', 'Microservices', 'Machine learning', 'UML']
  },
  {
    name: 'skills.categories.environments',
    skills: ['VS Code', 'Eclipse', 'NetBeans', 'Linux', 'Android Studio']
  },
  {
    name: 'skills.categories.office',
    skills: ['GitHub', 'Figma', 'MS Word', 'MS Excel', 'MS PowerPoint', 'Outlook']
  }
];

const SkillCategory: React.FC<{ category: SkillCategory; index: number }> = ({ category, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { t } = useLanguage();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="card"
    >
      <h3 className="text-xl font-bold text-secondary mb-4 break-words">{t(category.name)}</h3>
      <div className="flex flex-wrap gap-2">
        {category.skills.map((skill, i) => (
          <motion.span
            key={i}
            whileHover={{ scale: 1.05 }}
            className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm whitespace-nowrap"
          >
            {skill}
          </motion.span>
        ))}
      </div>
    </motion.div>
  );
};

const Skills: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <section id="skills" className="section-padding">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="heading-secondary mb-4 text-primary">{t('skills.title')}</h2>
          <div className="w-24 h-1 bg-secondary mx-auto rounded-full" />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {skillCategories.map((category, index) => (
            <SkillCategory key={index} category={category} index={index} />
          ))}
        </div>

        {/* Languages Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mt-8 sm:mt-12 text-center"
        >
          <h3 className="text-xl sm:text-2xl font-bold text-secondary mb-4 sm:mb-6">{t('skills.languages')}</h3>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-primary mb-1 sm:mb-2">{t('skills.french')}</div>
              <div className="text-sm sm:text-base text-base-content/60">{t('skills.fluent')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-primary mb-1 sm:mb-2">{t('skills.english')}</div>
              <div className="text-sm sm:text-base text-base-content/60">{t('skills.professional')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-primary mb-1 sm:mb-2">{t('skills.arabic')}</div>
              <div className="text-sm sm:text-base text-base-content/60">{t('skills.native')}</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills; 